import type { ReactNode } from 'react';
import { Badge } from '@/shared/ui/badge';
import type { AiCapabilities } from '@/api/schemas/ai';

/**
 * The four capability flags, in the order an operator reads them.
 *
 * `modelDiscovery` is OPTIONAL and says so here, in the hint: it is a
 * convenience that lets the console list a provider's models, never a
 * requirement. The model identifier is typed by hand either way.
 */
const CAPABILITY_FIELDS: ReadonlyArray<{
  key: keyof AiCapabilities;
  label: string;
  hint: string;
}> = [
  {
    key: 'structuredOutput',
    label: 'Structured output',
    hint: 'The model can be asked to answer against a JSON schema.',
  },
  {
    key: 'jsonMode',
    label: 'JSON mode',
    hint: 'The model can be asked to answer in JSON.',
  },
  {
    key: 'tools',
    label: 'Tools',
    hint: 'The model can call tools the console gives it.',
  },
  {
    key: 'modelDiscovery',
    label: 'Model discovery',
    hint: 'The provider can list its own models. Optional — the model identifier is typed by hand either way.',
  },
];

/**
 * The capabilities a provider HAS, as badges.
 *
 * Only the supported ones are shown. A badge carries its meaning in its TEXT,
 * never in its colour alone, so a list of supported capabilities reads
 * correctly without colour; a provider that supports nothing says so in words.
 */
export function CapabilityBadges({ capabilities }: { capabilities: AiCapabilities }): ReactNode {
  const supported = CAPABILITY_FIELDS.filter((field) => capabilities[field.key]);

  if (supported.length === 0) {
    return <Badge tone="neutral">No capabilities reported</Badge>;
  }

  return (
    <div className="flex flex-wrap gap-1">
      {supported.map((field) => (
        <Badge key={field.key} tone="positive">
          {field.label}
        </Badge>
      ))}
    </div>
  );
}

/**
 * The FULL capability set, supported or not.
 *
 * Used where the operator needs to know what a provider CANNOT do — the
 * provider detail screen. Each row states its answer in words ("Supported" /
 * "Not supported") so the meaning survives without colour.
 */
export function CapabilityList({ capabilities }: { capabilities: AiCapabilities }): ReactNode {
  return (
    <dl className="grid gap-3 sm:grid-cols-2">
      {CAPABILITY_FIELDS.map((field) => {
        const supported = capabilities[field.key];
        return (
          <div key={field.key}>
            <dt className="text-xs tracking-wide text-content-muted uppercase">{field.label}</dt>
            <dd className="text-sm text-content">
              <Badge tone={supported ? 'positive' : 'neutral'}>
                {supported ? 'Supported' : 'Not supported'}
              </Badge>
            </dd>
            <p className="mt-1 text-xs text-content-muted">{field.hint}</p>
          </div>
        );
      })}
    </dl>
  );
}

/**
 * The badge tone for a provider's lifecycle state.
 *
 * Narrower than `BadgeTone` on purpose: a detail page's status badge accepts
 * only the four tones it can render, and this function is used for one.
 */
export function providerStateTone(
  state: 'active' | 'disabled' | 'retired',
): 'neutral' | 'positive' | 'caution' {
  if (state === 'active') return 'positive';
  if (state === 'retired') return 'caution';
  return 'neutral';
}
